import { ArrowLeft, Buildings, Info } from "@phosphor-icons/react"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Skeleton } from "@/components/ui/Skeleton"
import { Tabs } from "@/components/ui/Tabs"
import { CniFacilitiesTable } from "@/features/cni-facilities/components/CniFacilitiesTable"
import { CniFirmTypeBadge } from "@/features/cni-firms/components/CniFirmTypeBadge"
import { useCniFirm } from "@/features/cni-firms/hooks/useCniFirm"

export const Route = createFileRoute("/_authed/cni/firms/$id")({
  component: CniFirmDetailPage,
})

function CniFirmDetailPage() {
  const { t, i18n } = useTranslation("cni-firms")
  const { t: tCommon } = useTranslation("common")
  const { id } = Route.useParams()
  const navigate = useNavigate()
  const { data, isLoading, error } = useCniFirm(id)
  const [tab, setTab] = useState("info")

  return (
    <div className="h-full flex flex-col min-h-0">
      <header className="border-b border-ok-border px-6 py-4 shrink-0">
        <div className="flex items-center gap-2 mb-2">
          <button
            type="button"
            onClick={() => void navigate({ to: "/cni/firms" })}
            className="inline-flex items-center gap-1 text-xs text-ok-foreground-muted hover:text-ok-foreground"
          >
            <ArrowLeft size={14} weight="bold" />
            {tCommon("actions.back")}
          </button>
          <span className="text-xs text-ok-foreground-subtle">/</span>
          <Link
            to="/cni/firms"
            className="text-xs text-ok-foreground-muted hover:text-ok-foreground"
          >
            {t("title")}
          </Link>
        </div>
        {isLoading ? (
          <Skeleton className="h-7 w-64" />
        ) : error ? (
          <p className="text-sm text-ok-critical">{(error as Error).message}</p>
        ) : data ? (
          <div className="flex items-start gap-4 flex-wrap">
            {data.logoUrl ? (
              <img
                src={data.logoUrl}
                alt=""
                width={48}
                height={48}
                className="rounded-ok bg-ok-bg-muted shrink-0"
              />
            ) : null}
            <div className="min-w-0 flex-1">
              <h1 className="text-lg font-semibold tracking-tight truncate">{data.name}</h1>
              <div className="mt-1 flex items-center gap-2 flex-wrap text-xs text-ok-foreground-muted">
                <CniFirmTypeBadge type={data.type} />
                <span>{`${data.city}, ${data.country}`}</span>
                <span>·</span>
                <span>
                  {t("detail.summary.facilityCount", { count: data.facilityCount })}
                </span>
                <span>·</span>
                <span>
                  {t("detail.summary.userCount", { count: data.userCount })}
                </span>
              </div>
            </div>
          </div>
        ) : null}
      </header>

      <div className="flex-1 flex flex-col min-h-0">
        <Tabs.Root
          value={tab}
          onValueChange={setTab}
          items={[
            { value: "info", label: t("tabs.info"), icon: <Info size={14} weight="regular" /> },
            {
              value: "facilities",
              label: t("tabs.facilities"),
              icon: <Buildings size={14} weight="regular" />,
            },
          ]}
        >
          <Tabs.Content value="info" className="p-6">
            {isLoading ? (
              <div className="space-y-3 max-w-xl">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            ) : data ? (
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 max-w-2xl text-sm">
                <Field label={t("detail.fields.country")} value={data.country} />
                <Field label={t("detail.fields.city")} value={data.city} />
                <Field
                  label={t("detail.fields.facilityCount")}
                  value={String(data.facilityCount)}
                  mono
                />
                <Field label={t("detail.fields.userCount")} value={String(data.userCount)} mono />
                <Field
                  label={t("detail.fields.createdAt")}
                  value={new Date(data.createdAt).toLocaleString(i18n.language)}
                  span2
                />
                <Field label={t("detail.fields.id")} value={data.id} mono span2 />
              </dl>
            ) : null}
          </Tabs.Content>
          <Tabs.Content value="facilities">
            {data ? (
              <CniFacilitiesTable
                firmId={data.id}
                routePath={`/cni/firms/${data.id}`}
                showFirmColumn={false}
              />
            ) : null}
          </Tabs.Content>
        </Tabs.Root>
      </div>
    </div>
  )
}

function Field({
  label,
  value,
  mono,
  span2,
}: {
  label: string
  value: string
  mono?: boolean
  span2?: boolean
}) {
  return (
    <div className={span2 ? "sm:col-span-2" : undefined}>
      <dt className="text-xs uppercase tracking-wide text-ok-foreground-subtle">{label}</dt>
      <dd className={`mt-0.5 text-ok-foreground ${mono ? "font-mono text-xs" : ""}`}>{value}</dd>
    </div>
  )
}
